import * as React from "react";
import type { Property, SnapshotContent } from "@/data/types";
import {
  formatPriceShort,
  formatBaths,
  formatStatus,
  locationLine,
} from "@/lib/format";
import { Reveal, Overline, CountUp } from "./primitives";

interface Props {
  property: Property;
  snapshot?: SnapshotContent;
}

interface Stat {
  label: string;
  node: React.ReactNode;
}

export const PropertySnapshot: React.FC<Props> = ({ property, snapshot }) => {
  const image = property.images?.[1] || property.images?.[0];
  const location = locationLine(property);
  const price = formatPriceShort(property.currentAskingPrice);
  const baths = formatBaths(property.bathrooms);
  const status = formatStatus(property.listingStatus);

  const stats: Stat[] = [];
  if (property.bedrooms != null)
    stats.push({ label: "Beds", node: property.bedrooms });
  if (baths != null) stats.push({ label: "Baths", node: baths });
  if (property.squareFeet != null)
    stats.push({
      label: "Approx. Sq Ft",
      node: property.squareFeet.toLocaleString("en-US"),
    });
  if (price) stats.push({ label: "Last Asking", node: price });
  if (property.daysOnMarket != null)
    stats.push({
      label: "Days on Market",
      node: <CountUp value={property.daysOnMarket} />,
    });
  if (status) stats.push({ label: "Status", node: status });

  return (
    <section
      className="w-full bg-ivory py-20 md:py-28"
      data-testid="property-snapshot"
      aria-label="The property at a glance"
    >
      <div className="mx-auto grid w-full max-w-5xl grid-cols-1 gap-12 px-6 md:px-10 lg:grid-cols-2 lg:items-center lg:gap-16">
        {image && (
          <Reveal className="order-1 lg:order-none">
            <figure className="overflow-hidden">
              <img
                src={image.url}
                alt={image.alt || property.address}
                loading="lazy"
                decoding="async"
                className="aspect-[4/5] w-full object-cover transition-transform duration-[1.2s] ease-out hover:scale-[1.03]"
              />
            </figure>
          </Reveal>
        )}

        <Reveal delay={0.1}>
          <Overline>What the market saw</Overline>
          <h2 className="mt-4 font-serif text-3xl leading-tight text-charcoal md:text-4xl">
            The listing as it was presented.
          </h2>
          <p className="mt-4 font-serif text-2xl text-charcoal-soft md:text-3xl">
            {property.address}
            {property.unit ? (
              <span>, {property.unit}</span>
            ) : null}
          </p>
          {location && (
            <p className="mt-2 text-sm uppercase tracking-[0.18em] text-charcoal-muted">
              {location}
            </p>
          )}

          {stats.length > 0 && (
            <dl className="mt-10 grid grid-cols-2 gap-y-8 border-y border-charcoal/10 py-8 sm:grid-cols-3">
              {stats.map((s) => (
                <div key={s.label} className="pr-4">
                  <dd className="font-serif text-2xl text-charcoal md:text-3xl">
                    {s.node}
                  </dd>
                  <dt className="mt-1 text-[0.7rem] uppercase tracking-[0.18em] text-charcoal-muted">
                    {s.label}
                  </dt>
                </div>
              ))}
            </dl>
          )}

          {snapshot?.observation && (
            <p className="mt-8 font-serif text-2xl italic leading-snug text-charcoal md:text-3xl">
              “{snapshot.observation}”
            </p>
          )}
          {snapshot?.strengths?.map((line, i) => (
            <p
              key={i}
              className="mt-4 max-w-prose text-base leading-relaxed text-charcoal-soft md:text-lg"
            >
              {line}
            </p>
          ))}
          {snapshot?.strengths && snapshot.strengths.length > 0 && (
            <p className="mt-6 max-w-prose text-base leading-relaxed text-charcoal-soft md:text-lg">
              That’s exactly why I wouldn’t start by assuming the marketing simply
              needed “more.” I would start by changing what buyers were being asked
              to notice.
            </p>
          )}
        </Reveal>
      </div>
    </section>
  );
};
